import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SEO } from '@/components/base/SEO';
import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';
import { Breadcrumb } from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedCategory, setSelectedCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setSelectedCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedCategory]);

  const currentCategory = categories.find((c) => c.slug === selectedCategory) || categories[0];
  const filteredArticles = articles.filter((a) => a.categorySlug === selectedCategory);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory.name,
    description: currentCategory.description,
    url: `https://korean-ethics.com/category/${selectedCategory}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SEO
        title={`${currentCategory.name} - 韩国伦理网`}
        description={currentCategory.description}
        keywords={`韩国伦理,${currentCategory.name},韩国社会道德,韩国文化`}
        canonicalUrl={`https://korean-ethics.com/category/${selectedCategory}`}
        ogTitle={`${currentCategory.name} - 韩国伦理网`}
        ogDescription={currentCategory.description}
        ogType="website"
        ogUrl={`https://korean-ethics.com/category/${selectedCategory}`}
      />

      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <section className="py-8 md:py-12">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed mb-8">
              {currentCategory.description}
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedCategory === cat.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-700'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-accent-300 transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={articleImages[article.slug]}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3 flex-wrap">
                      <span className="bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full">
                        {article.categoryName}
                      </span>
                      <span>{article.readTime}</span>
                    </div>
                    <h2 className="text-sm md:text-base font-semibold font-heading text-foreground-950 mb-2 group-hover:text-accent-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200">
                      <span className="text-xs text-foreground-500">{article.author}</span>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}